/** Tech app — take a picture (or pick from gallery) and add it to the ticket.  Lands in the ticket popup → Files → Pictures → Tech submitted.
 *  Design standard 2026-09-04.  Uses POST /api/tickets/{id}/attachments with source=tech. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { PhotoCamera as CameraIcon, PhotoLibrary as GalleryIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { attachmentsApi } from '@/services/api';
import { PictureGrid, uploadTicketFile, FileRow } from '../TicketFilesPanel';

export function TechPicturesCard({ ticketId }: { ticketId: number }) {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<FileRow[]>([]);
  const [counts, setCounts] = useState({ office: 0, customer: 0 });
  const [busy, setBusy] = useState(false);
  const cam = useRef<HTMLInputElement>(null); const gal = useRef<HTMLInputElement>(null);
  const load = useCallback(() => attachmentsApi.files(ticketId).then((r: any) => {
    setRows(r.data?.pictures?.tech || []); setCounts({ office: r.data?.pictures?.office?.length || 0, customer: r.data?.pictures?.customer?.length || 0 });
  }).catch(() => {}), [ticketId]);
  useEffect(() => { if (ticketId) load(); }, [ticketId, load]);
  const onFiles = async (list: FileList | null) => {
    if (!list || !list.length) return;
    setBusy(true);
    try { for (const f of Array.from(list)) await uploadTicketFile(ticketId, f, 'tech'); enqueueSnackbar(list.length > 1 ? `${list.length} pictures added` : 'Picture added to the ticket', { variant: 'success' }); await load(); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.detail || 'Upload failed', { variant: 'error' }); }
    finally { setBusy(false); if (cam.current) cam.current.value = ''; if (gal.current) gal.current.value = ''; }
  };
  const remove = async (r: FileRow) => { if (!window.confirm('Remove this picture?')) return; await attachmentsApi.remove(r.id); load(); };
  return (
    <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Pictures ({rows.length})</Typography>
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={e => onFiles(e.target.files)} />
        <input ref={gal} type="file" accept="image/*" multiple hidden onChange={e => onFiles(e.target.files)} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <CameraIcon />} disabled={busy} onClick={() => cam.current?.click()}
            sx={{ flex: 1, minHeight: 52, fontSize: 16, textTransform: 'none', bgcolor: '#0D2233', '&:hover': { bgcolor: '#123A54' } }}>Take photo</Button>
          <Button variant="outlined" startIcon={<GalleryIcon />} disabled={busy} onClick={() => gal.current?.click()} sx={{ minHeight: 52, textTransform: 'none' }}>Gallery</Button>
        </Box>
        <PictureGrid rows={rows} onDelete={remove} emptyText="No pictures yet — tap Take photo to add one to this ticket." />
        {(counts.office + counts.customer > 0) && (
          <Typography variant="caption" color="text.secondary">Also on this ticket: {counts.customer} customer picture(s), {counts.office} office picture(s) — see the ticket in Dispatch → Files.</Typography>
        )}
      </CardContent>
    </Card>
  );
}
